import { useState, useEffect, useCallback } from 'react';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
    MapPin, Users, Building2, Briefcase, Shield, MessageCircle,
    Search, RefreshCw, Plus, ExternalLink, Trash2, Edit3,
    TrendingUp, UserPlus, UserMinus, ToggleLeft, ToggleRight,
    Globe, CheckCircle, Filter, ArrowRight
} from 'lucide-react';

const SOURCE_ICONS = {
    corporate: Building2, job_market: Briefcase, military: Shield,
    social_media: MessageCircle, real_estate: MapPin,
};

const SOURCE_COLORS = {
    corporate: 'bg-violet-100 text-violet-700',
    job_market: 'bg-blue-100 text-blue-700',
    military: 'bg-green-100 text-green-700',
    social_media: 'bg-pink-100 text-pink-700',
    real_estate: 'bg-amber-100 text-amber-700',
};

const TYPE_COLORS = {
    buyer: 'bg-emerald-100 text-emerald-700',
    seller: 'bg-red-100 text-red-700',
    both: 'bg-purple-100 text-purple-700',
};

const STATUS_COLORS = {
    new: 'bg-zinc-100 text-zinc-600',
    reviewing: 'bg-blue-100 text-blue-700',
    contacted: 'bg-emerald-100 text-emerald-700',
    converted: 'bg-purple-100 text-purple-700',
    dismissed: 'bg-zinc-200 text-zinc-400',
};

export function RelocationLeadsTab() {
    const [leads, setLeads] = useState([]);
    const [markets, setMarkets] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searching, setSearching] = useState(false);
    const [filterMarket, setFilterMarket] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterSource, setFilterSource] = useState('');
    const [showAddMarket, setShowAddMarket] = useState(false);
    const [newMarket, setNewMarket] = useState({ name: '', state: '' });
    const [selectedLead, setSelectedLead] = useState(null);

    const loadData = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            if (filterMarket) params.set('market', filterMarket);
            if (filterType) params.set('type', filterType);
            if (filterSource) params.set('source', filterSource);

            const [leadsRes, marketsRes, statsRes] = await Promise.all([
                API.get(`/relocation/leads?${params.toString()}`),
                API.get('/relocation/markets'),
                API.get('/relocation/stats'),
            ]);
            setLeads(leadsRes.data);
            setMarkets(marketsRes.data);
            setStats(statsRes.data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, [filterMarket, filterType, filterSource]);

    useEffect(() => { loadData(); }, [loadData]);

    const searchLeads = async (marketId) => {
        setSearching(true);
        try {
            const body = marketId ? { market_id: marketId } : {};
            const { data } = await API.post('/relocation/search', body);
            alert(`Found ${data.leads_found} new leads${data.market ? ` in ${data.market}` : ''}`);
            await loadData();
        } catch (err) { alert(err.response?.data?.detail || 'Search failed'); }
        finally { setSearching(false); }
    };

    const toggleMarket = async (marketId, active) => {
        try {
            await API.put(`/relocation/markets/${marketId}`, { active: !active });
            await loadData();
        } catch (err) { console.error(err); }
    };

    const addMarket = async () => {
        if (!newMarket.name || !newMarket.state) return;
        try {
            await API.post('/relocation/markets', newMarket);
            setNewMarket({ name: '', state: '' });
            setShowAddMarket(false);
            await loadData();
        } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
    };

    const toggleAutoSearch = async () => {
        try {
            await API.post('/relocation/auto-search/toggle', {
                enabled: !stats?.auto_search_enabled
            });
            await loadData();
        } catch (err) { console.error(err); }
    };

    const updateLead = async (leadId, updates) => {
        try {
            await API.put(`/relocation/leads/${leadId}`, updates);
            await loadData();
        } catch (err) { console.error(err); }
    };

    const deleteLead = async (leadId) => {
        if (!window.confirm('Delete this lead?')) return;
        try {
            await API.delete(`/relocation/leads/${leadId}`);
            await loadData();
        } catch (err) { console.error(err); }
    };

    if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-zinc-400" /></div>;

    return (
        <div className="space-y-6" data-testid="relocation-leads-tab">
            {/* Stats Bar */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                    <StatCard label="Total Leads" value={stats.total} icon={Globe} color="zinc" />
                    <StatCard label="Buyers" value={stats.buyers} icon={UserPlus} color="emerald" />
                    <StatCard label="Sellers" value={stats.sellers} icon={UserMinus} color="red" />
                    <StatCard label="Both" value={stats.both} icon={Users} color="purple" />
                    <StatCard label="New" value={stats.new} icon={TrendingUp} color="blue" />
                    <StatCard label="Contacted" value={stats.contacted} icon={CheckCircle} color="amber" />
                </div>
            )}

            {/* Controls Row */}
            <div className="flex items-center gap-3 flex-wrap">
                <Button onClick={() => searchLeads()} disabled={searching}
                    className="bg-zinc-900 hover:bg-zinc-800 text-white text-xs" data-testid="search-all-markets-btn">
                    {searching ? <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Search className="w-3.5 h-3.5 mr-1.5" />}
                    {searching ? 'Searching...' : 'Search All Markets'}
                </Button>

                <button onClick={toggleAutoSearch}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${stats?.auto_search_enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-500'}`}
                    data-testid="auto-search-toggle">
                    {stats?.auto_search_enabled ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                    Auto-Search {stats?.auto_search_enabled ? 'ON' : 'OFF'}
                </button>

                <div className="flex-1" />

                {/* Filters */}
                <select value={filterMarket} onChange={e => setFilterMarket(e.target.value)}
                    className="h-8 text-xs border border-zinc-200 rounded-lg px-2" data-testid="filter-market">
                    <option value="">All Markets</option>
                    {markets.filter(m => m.active).map(m => (
                        <option key={m.id} value={m.name}>{m.name}</option>
                    ))}
                </select>
                <select value={filterType} onChange={e => setFilterType(e.target.value)}
                    className="h-8 text-xs border border-zinc-200 rounded-lg px-2" data-testid="filter-type">
                    <option value="">All Types</option>
                    <option value="buyer">Buyers</option>
                    <option value="seller">Sellers</option>
                    <option value="both">Both</option>
                </select>
                <select value={filterSource} onChange={e => setFilterSource(e.target.value)}
                    className="h-8 text-xs border border-zinc-200 rounded-lg px-2" data-testid="filter-source">
                    <option value="">All Sources</option>
                    <option value="corporate">Corporate</option>
                    <option value="job_market">Job Market</option>
                    <option value="military">Military</option>
                    <option value="social_media">Social Media</option>
                    <option value="real_estate">Real Estate</option>
                </select>
            </div>

            {/* Markets */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-zinc-700 font-['Outfit']">Markets</h4>
                    <Button size="sm" variant="ghost" onClick={() => setShowAddMarket(!showAddMarket)}
                        className="text-xs h-6" data-testid="add-market-btn">
                        <Plus className="w-3 h-3 mr-1" /> Add Market
                    </Button>
                </div>

                {showAddMarket && (
                    <div className="flex items-center gap-2 mb-3 bg-zinc-50 rounded-lg p-3">
                        <Input placeholder="City Name" value={newMarket.name}
                            onChange={e => setNewMarket(m => ({ ...m, name: e.target.value }))}
                            className="h-8 text-sm w-40" data-testid="new-market-name" />
                        <Input placeholder="ST" value={newMarket.state} maxLength={2}
                            onChange={e => setNewMarket(m => ({ ...m, state: e.target.value.toUpperCase() }))}
                            className="h-8 text-sm w-14" data-testid="new-market-state" />
                        <Button size="sm" onClick={addMarket} className="bg-emerald-600 text-white text-xs h-8">Add</Button>
                    </div>
                )}

                <div className="flex flex-wrap gap-2">
                    {markets.map(m => (
                        <div key={m.id}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${m.active ? 'bg-white border-zinc-300 text-zinc-800' : 'bg-zinc-50 border-zinc-100 text-zinc-400'}`}>
                            <MapPin className="w-3 h-3" />
                            <span>{m.name}, {m.state}</span>
                            {stats?.by_market?.[m.name] > 0 && (
                                <Badge className="bg-zinc-900 text-white text-[10px] px-1.5">{stats.by_market[m.name]}</Badge>
                            )}
                            <button onClick={() => toggleMarket(m.id, m.active)}
                                className={`${m.active ? 'text-emerald-600' : 'text-zinc-300'}`}
                                data-testid={`toggle-market-${m.id}`}>
                                {m.active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                            </button>
                            {m.active && (
                                <button onClick={() => searchLeads(m.id)} disabled={searching}
                                    className="text-zinc-400 hover:text-zinc-600" data-testid={`search-market-${m.id}`}>
                                    <Search className="w-3 h-3" />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Leads Grid */}
            {leads.length === 0 ? (
                <div className="bg-white rounded-2xl border border-zinc-200 p-12 text-center">
                    <Globe className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-zinc-900 font-['Outfit'] mb-2">No Relocation Leads Yet</h3>
                    <p className="text-sm text-zinc-500 mb-4">Click "Search All Markets" to find relocation signals in your active markets.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {leads.map(lead => {
                        const SourceIcon = SOURCE_ICONS[lead.source_category] || Globe;
                        return (
                            <div key={lead.id}
                                className="bg-white rounded-xl border border-zinc-200 p-4 hover:shadow-md transition-shadow cursor-pointer"
                                onClick={() => setSelectedLead(selectedLead?.id === lead.id ? null : lead)}
                                data-testid={`relo-lead-${lead.id}`}>
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-1.5">
                                        <Badge className={TYPE_COLORS[lead.lead_type] || 'bg-zinc-100'}>
                                            {lead.lead_type === 'buyer' ? <UserPlus className="w-3 h-3 mr-0.5" /> :
                                             lead.lead_type === 'seller' ? <UserMinus className="w-3 h-3 mr-0.5" /> :
                                             <Users className="w-3 h-3 mr-0.5" />}
                                            {lead.lead_type}
                                        </Badge>
                                        <Badge className={SOURCE_COLORS[lead.source_category] || 'bg-zinc-100'}>
                                            <SourceIcon className="w-3 h-3 mr-0.5" />
                                            {lead.source_category?.replace('_', ' ')}
                                        </Badge>
                                    </div>
                                    <Badge className={STATUS_COLORS[lead.status] || 'bg-zinc-100'}>{lead.status}</Badge>
                                </div>

                                <h4 className="text-sm font-medium text-zinc-900 line-clamp-2 mb-1">{lead.title}</h4>
                                <p className="text-xs text-zinc-500 line-clamp-2 mb-2">{lead.summary}</p>

                                <div className="flex items-center justify-between text-[10px] text-zinc-400">
                                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {lead.market}, {lead.state}</span>
                                    <span>{new Date(lead.created_at).toLocaleDateString()}</span>
                                </div>

                                {/* Expanded detail */}
                                {selectedLead?.id === lead.id && (
                                    <div className="mt-3 pt-3 border-t border-zinc-100 space-y-2" onClick={e => e.stopPropagation()}>
                                        <p className="text-xs text-zinc-600">{lead.summary}</p>
                                        <a href={lead.source_url} target="_blank" rel="noopener noreferrer"
                                            className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                                            <ExternalLink className="w-3 h-3" /> View Source
                                        </a>
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            {['new', 'reviewing', 'contacted', 'converted', 'dismissed'].map(s => (
                                                <button key={s} onClick={() => updateLead(lead.id, { status: s })}
                                                    className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${lead.status === s ? STATUS_COLORS[s] + ' ring-1 ring-offset-1 ring-zinc-300' : 'bg-zinc-50 text-zinc-400 hover:bg-zinc-100'}`}>
                                                    {s}
                                                </button>
                                            ))}
                                        </div>
                                        <div className="flex items-center gap-1.5 mt-1">
                                            <Button size="sm" variant="ghost" onClick={() => deleteLead(lead.id)}
                                                className="text-[10px] h-5 px-1.5 text-red-400">
                                                <Trash2 className="w-3 h-3 mr-0.5" /> Delete
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function StatCard({ label, value, icon: Icon, color }) {
    const colors = {
        zinc: 'bg-zinc-50 border-zinc-200', emerald: 'bg-emerald-50 border-emerald-200',
        red: 'bg-red-50 border-red-200', purple: 'bg-purple-50 border-purple-200',
        blue: 'bg-blue-50 border-blue-200', amber: 'bg-amber-50 border-amber-200',
    };
    const iconColors = {
        zinc: 'text-zinc-500', emerald: 'text-emerald-600', red: 'text-red-500',
        purple: 'text-purple-600', blue: 'text-blue-600', amber: 'text-amber-600',
    };
    return (
        <div className={`${colors[color]} border rounded-xl px-4 py-3 flex items-center gap-3`}>
            <Icon className={`w-5 h-5 ${iconColors[color]}`} />
            <div>
                <div className="text-lg font-semibold text-zinc-900 font-['Outfit']">{value}</div>
                <div className="text-[10px] text-zinc-500">{label}</div>
            </div>
        </div>
    );
}
